import { api } from "./client";

// Mirrors src/leadsniffer/api/serializers.py (AuthoringRunSummary etc.) —
// keep in sync by hand.

export type AuthoringRunStatus = "running" | "succeeded" | "failed";
export type AuthoringStepKind =
  | "plan"
  | "tool_call"
  | "message"
  | "proposal"
  | "error";

export interface AuthoringStep {
  at: string;
  kind: AuthoringStepKind;
  summary: string;
  data: Record<string, unknown>;
}

export interface AuthoringTokenUsage {
  requests: number;
  prompt_tokens: number;
  completion_tokens: number;
}

export interface SuggestedSample {
  url: string;
  expected: "positive" | "negative";
  reason: string | null;
}

export interface RemovedSample {
  url: string;
  sample_id: string;
  // A corpus sample's label is always set (non-nullable on the backend).
  expected: "positive" | "negative";
  reason: string | null;
}

export interface ProposedCheck {
  name: string;
  priority: number;
  action_id: string;
  positive_rules: Array<Record<string, unknown>>;
  negative_rules: Array<Record<string, unknown>>;
  shop_system_label: string | null;
  llm_verification_prompt: string | null;
  rationale: string | null;
  // Example domains the agent discovered + judged for the operator to add to
  // the verification corpus and re-run against. Absent on older runs.
  suggested_samples?: SuggestedSample[] | null;
  // Existing corpus samples the agent flags for removal (unreachable or
  // mislabeled/off-topic), with the sample_id so the operator can one-click
  // delete them. Absent on older runs.
  removed_samples?: RemovedSample[] | null;
}

export interface AuthoringRunSummary {
  id: string;
  check_id: string;
  started_at: string;
  finished_at: string | null;
  status: AuthoringRunStatus;
  brief: string | null;
  working_positive_rules: Array<Record<string, unknown>>;
  working_negative_rules: Array<Record<string, unknown>>;
  steps: AuthoringStep[];
  proposed_check: ProposedCheck | null;
  // True once the operator discarded this run's proposal. The proposal stays
  // on the run (audit record) but the panel hides it.
  dismissed: boolean;
  token_usage: AuthoringTokenUsage;
  error: string | null;
}

export interface AuthoringView {
  latest_run: AuthoringRunSummary | null;
}

export const getAuthoring = (checkId: string) =>
  api<AuthoringView>(`/api/checks/${checkId}/authoring`);

export const getAuthoringRun = (checkId: string, runId: string) =>
  api<AuthoringRunSummary>(`/api/checks/${checkId}/authoring/runs/${runId}`);

export const dismissAuthoringProposal = (checkId: string, runId: string) =>
  api<AuthoringRunSummary>(
    `/api/checks/${checkId}/authoring/runs/${runId}/dismiss`,
    { method: "POST" },
  );

export const startAuthoring = (
  checkId: string,
  body: {
    brief?: string | null;
    working_positive_rules: Array<Record<string, unknown>>;
    working_negative_rules: Array<Record<string, unknown>>;
  },
) =>
  api<{ run_id: string }>(`/api/checks/${checkId}/authoring/runs`, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const createDraftCheck = () =>
  api<{ id: string }>("/api/checks/draft", { method: "POST" });
